import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { CollectionStepCompletion } from "@/hooks/useCollections";

export interface CollectionMemoryEntry {
  id: string;
  added_at: string;
  added_by: string;
  memory: {
    id: string;
    title: string | null;
    memory_type: string;
    created_at: string;
    ai_chapter: string | null;
    raw_transcript: string | null;
    content_library_id: string | null;
  } | null;
}

export interface EnrichedStep {
  id: string;
  collection_template_id: string;
  content_library_id: string;
  order_index: number;
  required: boolean;
  repeatable: boolean;
  cooldown_days: number | null;
  step_title_override: string | null;
  content_library: {
    id: string;
    title: string | null;
    body: string | null;
    card_type: string | null;
    capture_type: string | null;
    instructions: string | null;
    tip: string | null;
    tags: string[] | null;
    voice_required: boolean | null;
  } | null;
  completion: CollectionStepCompletion | null;
}

export function useCollectionDetail(
  collectionId: string,
  templateId: string | null | undefined,
  isOpen: boolean,
) {
  const query = useQuery({
    queryKey: ["collection-detail", collectionId, templateId],
    enabled: isOpen && !!collectionId,
    staleTime: 0,
    queryFn: async () => {
      const [memResult, stepsResult, completionsResult] = await Promise.all([
        supabase
          .from("collection_memories")
          .select(
            "id, added_at, added_by, memory:memories(id, title, memory_type, created_at, ai_chapter, raw_transcript, content_library_id)",
          )
          .eq("collection_id", collectionId)
          .order("added_at", { ascending: false }),

        templateId
          ? supabase
              .from("collection_template_steps")
              .select(
                "id, collection_template_id, content_library_id, order_index, required, repeatable, cooldown_days, step_title_override, content_library:content_library(id, title, body, card_type, capture_type, instructions, tip, tags, voice_required)",
              )
              .eq("collection_template_id", templateId)
              .order("order_index")
          : Promise.resolve({ data: [] }),

        templateId
          ? supabase
              .from("collection_step_completions")
              .select("*")
              .eq("collection_id", collectionId)
          : Promise.resolve({ data: [] }),
      ]);

      const entries = (memResult.data ?? []) as CollectionMemoryEntry[];
      const rawSteps = (stepsResult.data ?? []) as Array<Omit<EnrichedStep, "completion">>;
      const completions = (completionsResult.data ?? []) as CollectionStepCompletion[];
      const enrichedSteps: EnrichedStep[] = rawSteps.map((step) => ({
        ...step,
        completion: completions.find((c) => c.step_id === step.id) ?? null,
      }));

      return { entries, enrichedSteps };
    },
  });

  return {
    data: query.data,
    entries: query.data?.entries ?? [],
    enrichedSteps: query.data?.enrichedSteps ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
